import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import { Stack } from "@mui/system";
import AddIcon from "@mui/icons-material/Add";
import { useEffect, useState } from "react";
import { DesignThemeEndPointLogic } from "../../../../../logic/AssetManagement/Design/Theme/DesignThemeEndPointLogic";
import {
  selectUserToken,
  setIsLoading,
} from "../../../../../features/userSlice";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
interface DuplicateThemeModalProps {
  open: boolean;
  onClose: any;
}
function DuplicateThemeModal({ open, onClose }: DuplicateThemeModalProps) {
  // -- Get Data From Redux
  const { selectedSharedUid, selectedThemeShareuid, designThemesData } =
    useSelector((state: any) => state?.theme);
  const dispatch = useDispatch();
  const oauthTokenHeader = useSelector(selectUserToken) as any;
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [, setShowErrorMessage] = useState(false); // State for error message visibility
  const [themeName, setThemeName] = useState(""); // State for theme name
  const [dictionary, setDictionary] = useState("");
  const [isValid, setIsValid] = useState(true);
  const [loading, setLoading] = useState(false); // State for loading
  const [singlePageContent, setSinglePageContent] = useState([]) as any;
  const [, setError] = useState(""); // State for error message
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const fetchSingleTheme = async () => {
      setLoading(true);
      try {
        if (selectedThemeShareuid !== null && selectedSharedUid !== null) {
          await DesignThemeEndPointLogic.getSingleDesignTheme(oauthTokenHeader,selectedThemeShareuid,setSinglePageContent,setError,navigate,location);
        } else {
          throw new Error("Unauthorized access for fetching single theme");
        }
      } catch (error) {
        console.error("Error fetching single theme", error);
      } finally {
        setLoading(false);
      }
    };
    fetchSingleTheme();
    // eslint-disable-next-line
  }, [selectedThemeShareuid]);

  useEffect(() => {
    if (open && singlePageContent) {
      setThemeName(`${singlePageContent?.name}_copy`); // Set the theme name to the selected theme name
      let value = singlePageContent?.dictionary;
      value = value
        ?.replace(/\\n/g, "")
        ?.replace(/\\\\/g, "")
        ?.replace(/\\"/g, '"'); // Remove "\n", "\\" and extra backslashes
      if (value?.startsWith('"') && value?.endsWith('"')) {
        // Remove starting and ending quotes
        value = value?.slice(1, -1); // Remove the first and last character
      }
      setDictionary(value);
    }
  }, [open, singlePageContent]);

  const duplicateTheme = async () => {
    const templateSharedUids = singlePageContent?.templates?.map((template: any) => template?.shared_uid) || [];
    
    const data = {
      website_shared_uid: selectedSharedUid,
      name: themeName,
      dictionary: dictionary,
      templates: templateSharedUids?.filter(
        (item: any) =>
          item !== null &&
          item !== undefined &&
          item !== "" &&
          !(Array.isArray(item) && item?.length === 0)
      ), // Filter out null, undefined, "", and []
    };
    try {
      setIsLoading(true);
      await DesignThemeEndPointLogic.createDesignTheme(oauthTokenHeader, data);
      setSnackbarOpen(true);
      setSnackbarMessage("Theme duplicated successfully");
      onClose();
      setIsLoading(true);
      await DesignThemeEndPointLogic.getDesignThemesData(
        oauthTokenHeader,
        selectedSharedUid,
        dispatch
      );
    } catch (error) {
      console.error("Error duplicating theme", error);
    } finally {
      setIsLoading(false);
      onClose();
    }
  };

  const validate = async () => {
    if (!themeName) {
      setSnackbarOpen(true);
      setSnackbarMessage("Please enter a theme name");
      setIsValid(false);
      return false;
    }

    // Check if themeName is at least 8 characters long and contains only letters and numbers
    const themeNameRegex = /^[a-zA-Z0-9-_&*$#!:|,./\\ ]{8,}$/;
    if (!themeNameRegex.test(themeName)) {
      setSnackbarOpen(true);
      setSnackbarMessage(
        "Template name must be at least 8 characters long and must contain only letters, numbers underscores and hyphens"
      );
      setIsValid(false);
      return false;
    }

    const isThemeNameUnique =
      Array.isArray(designThemesData) &&
      designThemesData?.every((item: any) => item.name !== themeName) &&
      designThemesData?.filter(
        (item: any) =>
          item?.name === themeName && item?.name !== singlePageContent?.name
      ).length === 0;

    if (!isThemeNameUnique) {
      setSnackbarOpen(true);
      setSnackbarMessage("Theme name must be unique");
      setIsValid(false);
      return false;
    }

    try {
      if (dictionary) {
        const cleanedDictionary = dictionary.replace(/\\n|\\+/g, ""); // Remove extra backslashes and newline characters
        const parsedAdditionalInfo = JSON.parse(cleanedDictionary);
        if (typeof parsedAdditionalInfo !== "object") {
          throw new Error("Not an object");
        }
      }
    } catch (e) {
      setSnackbarOpen(true);
      setSnackbarMessage("Dictionary must be a valid JSON object");
      setShowErrorMessage(true);
      setIsValid(false);
      return false;
    }

    setIsValid(true); // Validation passed
    return true; // Validation passed
  };

  const handleDuplicateTheme = async () => {
    const isFormValid = await validate();
    if (!isFormValid) return; // Only proceed if the form is valid
    try {
      setLoading(true);
      await duplicateTheme(); // Call the createNewTheme function
    } catch (error) {
      console.error(error, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDuplicateModal = () => {
    onClose();
  };

  const renderSnackbar = () => {
    return (
      <Snackbar
        sx={{ zIndex: "9999999" }}
        open={snackbarOpen}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert
          elevation={6}
          variant="filled"
          onClose={() => setSnackbarOpen(false)}
          severity={
            snackbarMessage.includes("Error") || isValid ? "success" : "error"
          }
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    );
  };

  return (
    <>
      {renderSnackbar()}
      <Dialog
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: "900px", maxWidth: "900px" } }}
      >
        <Paper sx={{ padding: "16px 24px 20px 24px" }}>
          <DialogTitle sx={{ padding: "0px 0px 16px 0px" }}>
            Duplicate Theme
          </DialogTitle>
          <TextField
            id="outlined-multiline-flexible"
            label="New Theme Name" // Set your label here
            value={loading ? "" : themeName}
            onChange={(e) => setThemeName(e.target.value)}
            placeholder="New name for the duplicated Theme" // Set your placeholder here
            multiline
            required
            color="secondary"
            size="small"
            maxRows={4}
            sx={{
              width: "852px",
            }}
          />
          <Typography
            sx={{ fontSize: "12px", marginTop: "8px", color: "#00000099" }}
          >
            All fields are mandatory.
          </Typography>
          <Stack
            flexDirection={"row"}
            justifyContent={"end"}
            marginTop={2}
            gap={1}
          >
            <Button
              color="secondary"
              onClick={handleCloseDuplicateModal}
            >
              Cancel
            </Button>
            <Button
              data-tracker="143"
              variant="contained"
              color="secondary"
              disabled={!themeName || themeName === ""}
              onClick={handleDuplicateTheme}
            >
              <Stack flexDirection={"row"} gap={1}>
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Create New"
                )}
                <AddIcon fontSize="small" />
              </Stack>
            </Button>
          </Stack>
        </Paper>
      </Dialog>
    </>
  );
}
export default DuplicateThemeModal;
